import { useCallback, useRef } from 'react'

import { Flex, SimpleSpinner, Text } from '@buffer-mono/popcorn'

import type { Channel } from '~publish/gql/graphql'
import { useEscapeKey } from '~publish/hooks/useEscapeKey'

import { CommentDeletedError } from '../CommentDeletedError'
import { AnimatedFormContent } from './AnimatedFormContent'
import { AnimatedTextarea } from './AnimatedTextarea'
import { DiscardReplyDialog } from './DiscardReplyDialog'
import { useCommentReply } from './hooks/useCommentReply'
import { ReplyFormFooter } from './ReplyFormFooter'
import { ReplyFormItem } from './ReplyFormItem'
import { CommentAvatar } from '../CommentItem/CommentAvatar'
import type { FilterStatus } from '../CommentsFilters/useFilters'
import { SuggestionButtons } from '../CommentsList/SuggestionButtons'
import { useCommentData } from '../context/CommentContext'

import styles from './CommentReplyForm.module.css'

const ErrorMessage = (): JSX.Element => (
  <Text size="sm" color="critical">
    An error occurred while replying to this comment
  </Text>
)

const SubmittingState = ({ replyText }: { replyText: string }): JSX.Element => (
  <Flex align="center" gap="2xs">
    <SimpleSpinner size="small" className={styles.spinner} />
    <Text color="subtle" className={styles.submittingText}>
      Sending: {replyText}
    </Text>
  </Flex>
)

type CommentReplyFormProps = {
  id?: string
  channel: Channel
  currentStatusFilter?: FilterStatus
  onAnimateOut: (shouldAnimate: boolean) => void
  initialExpanded?: boolean
}

interface AvatarCssProperties extends React.CSSProperties {
  '--connector-height': string
}

const getAvatarStyle = (isExpanded: boolean): AvatarCssProperties => ({
  '--connector-height': isExpanded ? 'var(--size-2xl)' : 'var(--size-sm)',
})

export const CommentReplyForm = ({
  id,
  channel,
  currentStatusFilter,
  onAnimateOut,
  initialExpanded = false,
}: CommentReplyFormProps): JSX.Element => {
  const comment = useCommentData()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const reply = useCommentReply({
    channel,
    currentStatusFilter,
    onAnimateOut,
    textareaRef,
    initialExpanded,
  })

  const {
    form: { text: replyText, isExpanded, expand, focus },
    submission: { isSubmitting, error, errorType, submit },
    ui: { showDiscardDialog, discard, keepWriting, setShowDiscardDialog },
    features: { insertSuggestion, insertSavedReply, savedRepliesRef },
    refs: { formRef },
  } = reply

  const handleSubmit = useCallback(
    (text: string): void => {
      submit(text).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Unexpected error in handleSubmit:', error)
      })
    },
    [submit],
  )

  const handleDiscardReply = useCallback((): void => {
    discard()
  }, [discard])

  useEscapeKey(handleDiscardReply, { isEnabled: isExpanded })

  if (errorType === 'not_found') {
    return <CommentDeletedError />
  }

  const submittingReplyText = reply.submission.pendingReply ?? replyText

  const formContent = (
    <form
      ref={formRef}
      aria-label={`Reply to comment from ${comment.author.username}`}
      onSubmit={(e): void => {
        e.preventDefault()
        handleSubmit(replyText)
      }}
    >
      <Flex direction="column" gap="xs" className={styles.replyContent}>
        {error && <ErrorMessage />}

        <AnimatedTextarea
          id={id}
          textareaRef={textareaRef}
          replyText={replyText}
          setReplyText={reply.form.setText}
          isExpanded={isExpanded}
          isLoading={isSubmitting}
          error={error}
          handleExpand={expand}
          handleFocus={focus}
          savedRepliesPopoverRef={savedRepliesRef}
          handleInsertSavedReply={insertSavedReply}
        />

        {isExpanded && !isSubmitting && (
          <ReplyFormFooter
            currentCharsCount={replyText.length}
            disabled={isSubmitting || !replyText}
            label={error ? 'Try Again' : 'Reply'}
          />
        )}
      </Flex>
    </form>
  )

  return (
    <>
      <ReplyFormItem
        avatar={
          <CommentAvatar
            hasParent={true}
            avatar={channel.avatar}
            username={channel.name}
            showDot={false}
            showConnector={false}
            style={getAvatarStyle(isExpanded)}
          />
        }
        suggestions={<SuggestionButtons onSuggestionClick={insertSuggestion} />}
        hasSuggestions={isExpanded}
        className={styles.replyTextArea}
      >
        <AnimatedFormContent
          isReplying={isSubmitting}
          isLoading={isSubmitting}
          replyingContent={<SubmittingState replyText={submittingReplyText} />}
          formContent={formContent}
        />
      </ReplyFormItem>

      <DiscardReplyDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        onDiscard={handleDiscardReply}
        onKeepWriting={keepWriting}
      />
    </>
  )
}
